import { toast } from "react-toastify";
import WebService from "../../utility/WebService";

type QuillLikeRef = React.RefObject<{
  getEditor: () => {
    getSelection: (focus?: boolean) => { index: number; length: number } | null;
    insertText: (index: number, text: string, formats?: any) => void;
    setSelection: (index: number, length?: number) => void;
    getLength?: () => number;
  };
}>;

type Options = {
  storage?: string;       // default "EVENTS"
  fieldName?: string;     // default "file"
  onUploaded?: (url: string, fileName: string) => void;
};

export const createQuillDocumentHandler = (
  editorRef: QuillLikeRef,
  options: Options = {}
) => {
  const {
    storage = "EVENTS",
    fieldName = "file",
    onUploaded,
  } = options;

  return () => {
    if (typeof document === "undefined") return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      ".pdf,.doc,.docx," +
      "application/pdf," +
      "application/msword," +
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const quillWrapper = editorRef.current;
      if (!quillWrapper?.getEditor) {
        toast.error("Editor not ready");
        return;
      }

      const formData = new FormData();
      formData.append(fieldName, file);
      formData.append("storage", storage);

      try {
        const res: any = await WebService.fileUploadAPI({
          action: "upload",
          body: formData,
        });

        const fileUrl = res?.data?.url;
        if (!fileUrl) {
          toast.error("Document URL not returned");
          return;
        }

        onUploaded?.(fileUrl, file.name);

        // Ask the user for a custom link title (filename is the default)
        const customTitle = window.prompt("Enter link title:", file.name);
        if (customTitle === null) return; // user cancelled

        const editor = quillWrapper.getEditor();
        const range = editor.getSelection(true);
        const insertAt =
          range?.index ??
          (editor.getLength?.() ? editor.getLength() - 1 : 0);

        const displayText = `\uD83D\uDCC4 ${customTitle.trim() || file.name}`;
        editor.insertText(insertAt, displayText, { link: fileUrl });
        // Insert a plain space after so the cursor exits the link format
        editor.insertText(insertAt + displayText.length, " ");
        editor.setSelection(insertAt + displayText.length + 1, 0);
      } catch {
        toast.error("Document upload failed");
      }
    };
  };
};
